import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resendVerification } from '../services/auth';
import '../styles/Auth.css';

export const VerifyEmailPage: React.FC = () => {
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user, firebaseUser, refreshUser } = useAuth();
  const navigate = useNavigate();

  // If user is already verified, redirect to dashboard
  useEffect(() => {
    if (user?.email_verified) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Poll for email verification status
  useEffect(() => {
    const checkVerification = async () => {
      if (firebaseUser) {
        // Reload Firebase user to get latest email verification status
        await firebaseUser.reload();
        if (firebaseUser.emailVerified) {
          // Force-refresh the Firebase ID token so the backend sees
          // the updated email_verified claim (cached tokens are stale)
          await firebaseUser.getIdToken(true);
          // Sync with backend
          await refreshUser();
          navigate('/dashboard');
        }
      }
    };

    const interval = setInterval(checkVerification, 3000);
    return () => clearInterval(interval);
  }, [firebaseUser, refreshUser, navigate]);

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    setMessage('');

    try {
      await resendVerification();
      setMessage('Verification email sent! Please check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="email-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h1>Check Your Email</h1>
          <p>
            We've sent a verification link to<br />
            <strong>{firebaseUser?.email || user?.email}</strong>
          </p>
        </div>

        {message && (
          <div className="auth-success">
            {message}
          </div>
        )}

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <div className="verify-instructions">
          <p>
            Click the link in your email to verify your account.
            Once verified, you'll be able to start practicing math.
          </p>
          <p className="small-text">
            Didn't receive the email? Check your spam folder or click below to resend.
          </p>
        </div>

        <button
          onClick={handleResend}
          className="auth-button secondary"
          disabled={isResending}
        >
          {isResending ? 'Sending...' : 'Resend Verification Email'}
        </button>

        <div className="auth-footer">
          <p>
            Wrong email?{' '}
            <button onClick={() => navigate('/register')} className="link-button">
              Sign up with a different email
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
